//
// Copyright (c) 2011-2017 by Curalate, Inc.
//

import { getExtensionForMimeType } from '@utils/mimeTypes';

import template from './csv-download.html';

(function() {
  // Supports Chrome 31+, Firefox 31+, Safari 7+, IE10+
  // http://caniuse.com/#search=URL
  const URL = (window.URL || window.webkitURL);

  // This directive supports intermediate download state for Chrome 31+, Firefox 31+, and IE10+
  // It defaults to "normal" download behavior for Safari
  // This directive cannot be used when the resource is accessed via a cross domain reference without
  // enabling CORS support on the server-side. An alternative is to use the native download attribute on
  // an anchor element, since Chrome does not apply CORS restrictions to it.
  angular.module('curalate').directive('downloadable', [
    '$http',
    '$httpParamSerializer',
    '$timeout',
    'TrackingService',
    function(
      $http,
      $httpParamSerializer,
      $timeout,
      TrackingService,
    ) {
      return {
        restrict: 'A',
        scope: {
          downloadable: '=',
          downloadableParams: '&',
          onDownloadTriggered: '&',
          onDownloadStarted: '&?',
          linkTargetId: '@?',
          isPostRequest: '<',
          filename: '=',
          trackCategory: '@',
        },
        replace: false,
        link(scope, element, attrs) {
          const saveBlob = (function() {
            const a = document.createElement('a');
            const dest = document.getElementById(scope.linkTargetId) || document.body;
            a.setAttribute('style', 'display: none');
            dest.appendChild(a);

            return function(blob, fileName, headers) {
              const csv = 'application/csv';
              const contentType = headers['content-type'] || csv;
              const binaryData = [];
              binaryData.push(blob);
              const url = URL.createObjectURL(new Blob(binaryData, { type: contentType }));

              // If the filename doesn't include an extension, try to extrapolate one from the contentType
              if (!fileName.includes('.')) {
                const mimeType = contentType.split(';')[0];
                const extension = getExtensionForMimeType(mimeType) ?? 'csv';
                fileName += `.${extension}`;
              }

              a.setAttribute('href', url);

              // Use native MSIE method if applicable
              if (window.navigator.msSaveBlob) {
                angular.element(a).on('click', () => {
                  window.navigator.msSaveBlob(blob, fileName);
                  angular.element(a).off('click');
                });
              } else {
                a.setAttribute('download', fileName);
              }

              if (scope.linkTargetId) {
                // Assume that we are in an Angular context. In this case, we need to synchronize with
                // the $digest loop. Run this inside of a $timeout without running $apply. This is fine
                // as the specific click doesn't update any visual state that needs an immediate refresh.
                $timeout(() => {
                  a.click();

                  // Appease Firefox by waiting 5 seconds before revoking the object URL to free memory
                  $timeout(() => {
                    URL.revokeObjectURL(url);
                  }, 5000);
                }, 0, false /* Run $apply */);
              } else {
                a.click();

                // Appease Firefox by waiting 5 seconds before revoking the object URL to free memory
                $timeout(() => {
                  URL.revokeObjectURL(url);
                }, 5000);
              }
            };
          })();

          angular.element(element).on('click', () => {
            if (!attrs.disabled) {
              let httpPromise;

              if (scope.trackCategory) {
                TrackingService.recordEvent(scope.trackCategory, 'DownloadClick', scope.filename);
              }

              const responseHandler = function(response) {
                if (scope.onDownloadStarted) {
                  scope.onDownloadStarted()();
                }

                const headers = response.headers();

                const contentDisposition = headers['content-disposition'] || '';
                const match = contentDisposition.match(/filename="(.*?)"/);
                const contentDispositionFilename = match && match[1];

                const filename = scope.filename || contentDispositionFilename;

                saveBlob(response.data, filename, headers);
              };

              if (scope.isPostRequest) {
                httpPromise = $http
                  .post(scope.downloadable, scope.downloadableParams(), { responseType: 'arraybuffer' })
                  .then(response => responseHandler(response));
              } else {
                httpPromise = $http
                  .get(scope.downloadable, { responseType: 'blob', params: scope.downloadableParams() })
                  .then(response => responseHandler(response));
              }

              scope.onDownloadTriggered()(httpPromise);
            }
          });
        },
      };
    }]).directive('csvDownload', () => {
      return {
        restrict: 'E',
        scope: {
          classes: '@',
          downloadUrl: '&',
          downloadParams: '&',
          onDownloadTriggered: '&',
          filename: '=',
          downloadToggle: '=',
          buttonText: '@',
          downloadingText: '@',
          iconOnly: '<',
          hideIcon: '<',
          trackCategory: '@',
          isDisabled: '<',
          isPostRequest: '<',
          disableWhileDownloading: '=',
        },
        template,
        replace: true,
      };
    });
})();
